import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import MyCompetencySkillProficiencyService from '../Service/SMESSISkillMappingService';

const proficiencyOptions = [
  "P0: Not Assessed",
  "P1: Beginner",
  "P2: Intermediate",
  "P3: Advanced",
  "P3+: Expert Eligible",
  "P4: Expert",
  "P4+: Master Eligible",
  "P5: Master"
];
 

const SkillRow = ({ title, color, allowMultiple = false, showDelete = false, user }) => {
  const initialSkill = {
    smeMyCompetencySkillId: "",
    skillName: "",
    skillType: title,
    proficiencyLevel: "P0: Not Assessed",
    completionDate: "",
    expiryDate: "",
    userId: user?.userId,
    userName: user?.userName,
  };

  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState({ ...initialSkill });
  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState([]);

  const formatInputDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return "";
    return d.toISOString().split("T")[0];
  };

  let typingTimeout;
  const fetchSkillSuggestions = async (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }
    clearTimeout(typingTimeout);
    typingTimeout = setTimeout(async () => {
      try {
        const response = await MyCompetencySkillProficiencyService.searchSSISkillNames(query);
        setSuggestions(response || []);
      } catch (error) {
        console.error("Failed to fetch suggestions:", error.message);
      }
    }, 300);
  };

  useEffect(() => {
    const fetchSkills = async () => {
      if (user?.userId) {
        try {
          const response = await MyCompetencySkillProficiencyService.getAllSkillsByUserAndSkillType(user.userId, title);
          const formatted = response.map(skill => ({
            savedSkill: skill,
            isEditing: false,
            editingData: {
              ...skill,
              completionDate: formatInputDate(skill.completionDate),
              expiryDate: formatInputDate(skill.expiryDate),
            },
            errors: {},
          }));
          setSkills(formatted);
        } catch (error) {
          console.error("Error loading skills:", error.message);
        }
      }
    };
    fetchSkills();
  }, [user, title]);

  const validateSkill = (skill) => {
    const validationErrors = {};
    if (!skill.skillName.trim()) validationErrors.skillName = true;
    if (!skill.proficiencyLevel.trim()) validationErrors.proficiencyLevel = true;
    if (!skill.completionDate) validationErrors.completionDate = true;
    if (!skill.expiryDate) validationErrors.expiryDate = true;
    return validationErrors;
  };

  const handleAddSkill = async () => {
    const validationErrors = validateSkill(newSkill);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      const savedSkill = await MyCompetencySkillProficiencyService.addMyCompetencyProficiencySkill(newSkill);
      const addedSkill = {
        savedSkill,
        isEditing: false,
        editingData: {
          ...savedSkill,
          completionDate: formatInputDate(savedSkill.completionDate),
          expiryDate: formatInputDate(savedSkill.expiryDate),
        },
        errors: {},
      };
      setSkills([...skills, addedSkill]);
      setNewSkill({ ...initialSkill });
    } catch (error) {
      console.error("Failed to add skill:", error.message);
    }
  };

  const handleUpdateSkill = async (index) => {
    const skill = skills[index].editingData;
    const validationErrors = validateSkill(skill);
    if (Object.keys(validationErrors).length > 0) {
      const updatedSkills = [...skills];
      updatedSkills[index].errors = validationErrors;
      setSkills(updatedSkills);
      return;
    }
    try {
      const updated = await MyCompetencySkillProficiencyService.updateMyCompetencyProficiencySkill(skill);
      const updatedSkills = [...skills];
      updatedSkills[index] = {
        savedSkill: updated,
        isEditing: false,
        editingData: { ...updated },
        errors: {},
      };
      setSkills(updatedSkills);
    } catch (error) {
      console.error("Failed to update skill:", error.message);
    }
  };

  const handleDeleteSkill = async (index) => {
    const id = skills[index].savedSkill.skillId;
    try {
      await MyCompetencySkillProficiencyService.deleteMyCompetencyProficiencySkill(id);
      const updatedSkills = [...skills];
      updatedSkills.splice(index, 1);
      setSkills(updatedSkills);
    } catch (error) {
      console.error("Failed to delete skill:", error.message);
    }
  };

  const handleChange = (index, e, isNew = false) => {
    const { name, value } = e.target;
    if (isNew) {
      setNewSkill(prev => {
        const updated = { ...prev, [name]: value };
        setErrors(prevErrors => ({ ...prevErrors, [name]: false }));
        return updated;
      });
      if (name === "skillName") fetchSkillSuggestions(value);
    } else {
      const updatedSkills = [...skills];
      updatedSkills[index].editingData[name] = value;
      if (updatedSkills[index].errors) {
        updatedSkills[index].errors[name] = false;
      }
      setSkills(updatedSkills);
    }
  };

  const handleEditSkill = (index) => {
    const updatedSkills = [...skills];
    updatedSkills[index].isEditing = true;
    setSkills(updatedSkills);
  };

  const handleCancelEdit = (index) => {
    const updatedSkills = [...skills];
    updatedSkills[index].editingData = { ...updatedSkills[index].savedSkill };
    updatedSkills[index].isEditing = false;
    updatedSkills[index].errors = {};
    setSkills(updatedSkills);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="mb-8 border-b border-gray-300 pb-6">
      <label className={`w-32 text-base font-semibold`} style={{ color }}>{title}</label>
      {(allowMultiple || skills.length === 0) && (
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <div className="relative">
            <label className="text-sm font-semibold">
              Skill Name  {errors.skillName && <span className="text-red-600"> *</span>}
            </label>
            <input
              type="text"
              name="skillName"
              value={newSkill.skillName}
              onChange={e => handleChange(null, e, true)}
              autoComplete="off"
              className={`border rounded-md px-3 py-1.5 text-sm w-48 ${errors.skillName ? 'border-red-500' : 'border-gray-300'}`}
            />
            {suggestions.length > 0 && (
              <ul className="absolute top-full left-0 bg-white border border-gray-300 z-10 w-full max-h-40 overflow-auto rounded shadow-md">
                {suggestions.map((s, idx) => (
                  <li
                    key={idx}
                    onClick={() => {
                      setNewSkill(prev => ({ ...prev, skillName: s }));
                      setSuggestions([]);
                    }}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Proficiency  {errors.proficiencyLevel && <span className="text-red-600"> *</span>}
            </label>
            <select
              name="proficiencyLevel"
              value={newSkill.proficiencyLevel}
              onChange={e => handleChange(null, e, true)}
              className={`border rounded-md px-3 py-1.5 text-sm ${errors.proficiencyLevel ? 'border-red-500' : 'border-gray-300'}`}
            >
              {proficiencyOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Completion  {errors.completionDate && <span className="text-red-600"> *</span>}
            </label>
            <input
              type="date"
              name="completionDate"
              value={newSkill.completionDate}
              onChange={e => handleChange(null, e, true)}
              className={`border rounded-md px-3 py-1.5 text-sm ${errors.completionDate ? 'border-red-500' : 'border-gray-300'}`}
            />
          </div>

          <div>
            <label className="text-sm font-semibold">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Expiry  {errors.expiryDate && <span className="text-red-600"> *</span>}
            </label>
            <input
              type="date"
              name="expiryDate"
              value={newSkill.expiryDate}
              onChange={e => handleChange(null, e, true)}
              className={`border rounded-md px-3 py-1.5 text-sm ${errors.expiryDate ? 'border-red-500' : 'border-gray-300'}`}
            />
          </div>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <button
            onClick={handleAddSkill}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md text-sm self-end"
          >
            Add Skill
          </button>
        </div>
      )}

      <div className="space-y-3 mt-4">
        {skills.map((skill, index) => (
          <div key={index} className="bg-white border rounded-xl shadow-sm p-3">
            {skill.isEditing ? (
              <div className="grid grid-cols-[200px_150px_180px_180px_auto] gap-x-4 items-center">
                <input
                  type="text"
                  name="skillName"
                  value={skill.editingData.skillName}
                  onChange={e => handleChange(index, e)}
                  className={`border rounded-md px-3 py-1.5 text-sm ${skill.errors?.skillName ? 'border-red-500' : 'border-gray-300'}`}
                />
                <select
                  name="proficiencyLevel"
                  value={skill.editingData.proficiencyLevel}
                  onChange={e => handleChange(index, e)}
                  className={`border rounded-md px-3 py-1.5 text-sm ${skill.errors?.proficiencyLevel ? 'border-red-500' : 'border-gray-300'}`}
                >
                  {proficiencyOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <input
                  type="date"
                  name="completionDate"
                  value={skill.editingData.completionDate}
                  onChange={e => handleChange(index, e)}
                  className={`border rounded-md px-3 py-1.5 text-sm ${skill.errors?.completionDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                <input
                  type="date"
                  name="expiryDate"
                  value={skill.editingData.expiryDate}
                  onChange={e => handleChange(index, e)}
                  className={`border rounded-md px-3 py-1.5 text-sm ${skill.errors?.expiryDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                <div className="flex gap-2">
                  <button onClick={() => handleUpdateSkill(index)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm">Update</button>
                  <button onClick={() => handleCancelEdit(index)} className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1.5 rounded-md text-sm">Cancel</button>
                  {showDelete && (
                    <button onClick={() => handleDeleteSkill(index)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md text-sm">Delete</button>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-[300px_200px_200px_200px_auto] gap-x-2 items-center text-sm">
                <div><span className="font-semibold">Skill:</span> {skill.savedSkill.skillName}</div>
                <div><span className="font-semibold">Proficiency:</span> {skill.savedSkill.proficiencyLevel}</div>
                <div><span className="font-semibold">Completion:</span> {formatDate(skill.savedSkill.completionDate)}</div>
                <div><span className="font-semibold">Expiry:</span> {formatDate(skill.savedSkill.expiryDate)}</div>
                <div className="flex gap-2">
                  <button onClick={() => handleEditSkill(index)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-sm">Update</button>
                  {showDelete && (
                    <button onClick={() => handleDeleteSkill(index)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md text-sm">Delete</button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SMESSISkillMapping = () => {
  const location = useLocation();
  const { user } = location.state || {};

  return (
    <div className="border border-blue-900 p-6 max-w-7xl mx-auto font-sans">
      <h2 className="text-3xl font-bold text-center text-black-900 mb-10">Skill and Proficiency Details</h2>
      <SkillRow title="Primary" color="purple" user={user} />
      <SkillRow title="Industry" color="brown" user={user} />
      <SkillRow title="Secondary" color="indigo" allowMultiple={true} showDelete={true} user={user} />
    </div>
  );
};

export default SMESSISkillMapping;
